/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import { v4 } from 'uuid';
import React, { useEffect, useReducer, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import i18next from 'i18next';
import { I18nextProvider } from 'react-i18next';
import {
  FlowBlockTerminalPage,
  FlowBlockCustomerContact,
} from '@staffino/model';
import { IFlowBridge } from './flow-bridge';
import FlowBridgeContext from './flow-bridge-context';
import FlowExecutor from './flow-executor';
import useObjects from './use-objects-hook';

const customerContactDefinition = {
  type: 'customer-contact',
  padding: '1rem',
  id: v4(),
  children: [],
};

function FlowLoader({ flowId, bridge, platform }) {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const i18Ref = useRef(undefined);
  const [translationsObject] = useObjects('translations', {
    localeId: bridge.localeId,
  });
  const locales = useObjects('locale');
  const locale = locales?.find((l) => l.id === bridge.localeId) || locales?.[0];
  const organization = bridge.objects?.find(
    (fo) => fo.objectType === 'organization',
  );

  useEffect(() => {
    const changeLanguage = () => {
      if (translationsObject?.localeId === bridge.localeId && i18Ref.current) {
        i18Ref.current.addResourceBundle(
          locale.code,
          'customer-app',
          translationsObject?.translations ?? {},
        );
        i18Ref.current.changeLanguage(locale.code);
      }
    };
    if (locale) {
      if (!i18next.isInitialized) {
        i18next
          .init({
            lng: locale.code,
            ns: 'customer-app',
            nsSeparator: false,
            keySeparator: false,
            react: {
              useSuspense: false,
            },
            resources: {
              [locale.code]: {
                'customer-app': translationsObject?.translations,
              },
            },
          })
          .then(() => {
            i18Ref.current = i18next;
            changeLanguage();
            forceUpdate();
          });
      } else {
        i18Ref.current = i18next;
        changeLanguage();
        forceUpdate();
      }
    }
  }, [translationsObject, locale, bridge.localeId]);
  const [flow] = useObjects('flow', { id: flowId });
  const [customer] = useObjects('customer');
  // injects customer contact form to terminal-page definition if allowed
  if (organization?.contactCollectionAllowed && customer?.email) {
    const terminalPage = flow?.definition?.children?.find(
      (c) => c.type === 'terminal-page',
    ) as FlowBlockTerminalPage;
    if (
      terminalPage &&
      'children' in terminalPage &&
      Array.isArray(terminalPage?.children)
    ) {
      const customerContact = terminalPage.children.find?.(
        (c: any) => c.type === 'customer-contact',
      );
      if (customerContact) {
        terminalPage.children.pop(); // fixes strange behavior, hopefully it will stay as last item in array forever
        terminalPage.children.push({
          ...customerContact,
          type: 'customer-contact' as const,
          customerEmail: customer?.email,
          organizationId: organization.id,
          targetId: bridge?.targetId,
        } as FlowBlockCustomerContact);
      } else {
        // this will act as default, not necessary to add to flow definition
        const newCustomerContact = {
          ...customerContactDefinition,
          type: 'customer-contact' as const,
          customerEmail: customer?.email,
          organizationId: organization.id,
          targetId: bridge?.targetId,
          width: '100%',
          image:
            'https://s3.eu-west-1.amazonaws.com/cdn.staffino.com/flow-assets/244502fa-df1c-4d54-9039-2c38880b5a88/collection-banner.png',
        };
        terminalPage.children.push(newCustomerContact);
      }
    }
  }

  if (!i18Ref.current || !flow) {
    return null;
  }
  return (
    <I18nextProvider i18n={i18Ref.current} defaultNS="customer-app">
      <FlowExecutor
        definition={flow.definition}
        status={bridge.status}
        platform={platform}
      />
    </I18nextProvider>
  );
}

type Props = {
  flowId: string;
  bridge: IFlowBridge;
  platform?: string;
};
function FlowContainer({ flowId, bridge, platform }: Props) {
  return (
    <FlowBridgeContext.Provider value={{ bridge }}>
      <FlowLoader flowId={flowId} bridge={bridge} platform={platform} />
    </FlowBridgeContext.Provider>
  );
}

FlowContainer.render = (props: Props, rootElement) => {
  const root = createRoot(rootElement);
  root.render(<FlowContainer {...props} />);
};

export default FlowContainer;
